import {useEffect, useMemo, useState} from "react";
import {Box, Dialog, DialogTitle, DialogContent, Divider, IconButton, Typography} from "@mui/material";
import {DescriptionRounded, Close as CloseIcon} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import {useTranslation} from "react-i18next";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import CustomLoader from "../../../../../util/component/CustomLoader";
import {getAuditLog} from "../../../api/templateApi";
import {AuditEntry} from "../../../types";

dayjs.extend(utc);

interface TabChangelogProps {
    templateName: string;
}

const TabChangelog = ({templateName}: TabChangelogProps) => {
    const {t} = useTranslation("templates");
    const [rowData, setRowData] = useState<AuditEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);

    useEffect(() => {
        setLoading(true);
        getAuditLog(templateName, 0, 100)
            .then((data) => {
                // Filter out type, tenant_id, description (always)
                // Filter out is_active only for CREATE action
                const filtered = (data.content || []).filter(
                    entry => {
                        if (entry.field === 'type' || entry.field === 'tenant_id' || entry.field === 'description') {
                            return false;
                        }
                        if (entry.field === 'is_active' && entry.action === 'CREATE') {
                            return false;
                        }
                        return true;
                    }
                );
                setRowData(filtered);
            })
            .catch(() => setRowData([]))
            .finally(() => setLoading(false));
    }, [templateName]);

    const columnDefs = useMemo<ColDef<AuditEntry>[]>(() => [
        {
            field: "changedAt",
            headerName: t('changelog.columns.date'),
            sortable: true,
            sort: 'desc',
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<AuditEntry, string>) => {
                if (!params.value) return '';
                const val = params.value;
                // Handle both ISO string and epoch (seconds or milliseconds)
                const d = typeof val === 'number'
                    ? dayjs.unix(val < 1e12 ? val : val / 1000)
                    : dayjs.utc(val);
                return d.local().format('DD-MM-YYYY HH:mm');
            }
        },
        {
            field: "changedBy",
            headerName: t('changelog.columns.user'),
            flex: 1,
        },
        {
            field: "action",
            headerName: t('changelog.columns.action'),
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<AuditEntry, string>) =>
                params.value ? t(`changelog.actions.${params.value}`, params.value) : '-'
        },
        {
            field: "field",
            headerName: t('changelog.columns.field'),
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<AuditEntry, string>) =>
                params.value ? t(`changelog.fields.${params.value}`, params.value) : '-'
        },
        {
            field: "versionNumber",
            headerName: t('changelog.columns.version'),
            flex: 0.5,
            valueFormatter: (params: ValueFormatterParams<AuditEntry, number>) =>
                params.value != null ? String(params.value) : '-'
        },
        {
            field: "actions",
            headerName: '',
            width: 60,
            maxWidth: 60,
            minWidth: 60,
            sortable: false,
            resizable: false,
            suppressMenu: true,
            cellStyle: {
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                backgroundColor: '#f5f5f5'
            },
            cellRenderer: (params: any) => {
                const handleClick = () => {
                    setSelectedEntry(params.data);
                    setDetailDialogOpen(true);
                };
                return (
                    <IconButton
                        size="small"
                        onClick={handleClick}
                        sx={{ 
                            color: 'accent.main',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        <DescriptionRounded fontSize="small" />
                    </IconButton>
                );
            }
        },
    ], [t]);

    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: true,
        unSortIcon: true,
    }), []);

    return (
        <>
            <Box sx={{mt: 3}}>
                <AgGridCustomTable<AuditEntry>
                    height="500px"
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowData={rowData}
                    pagination={true}
                    paginationPageSize={10}
                    suppressHorizontalScroll={true}
                    suppressRowHoverHighlight={true}
                    onGridSizeChanged={(params) => {
                        params.api.sizeColumnsToFit();
                    }}
                    onFirstDataRendered={(params) => {
                        params.api.sizeColumnsToFit();
                    }}
                />
                <CustomLoader loading={loading}/>
            </Box>

            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth={selectedEntry?.action === 'ACTIVATE' ? 'sm' : 'xl'}
                fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {t('changelog.detailDialog.title')}
                    <IconButton
                        onClick={() => setDetailDialogOpen(false)}
                        size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedEntry?.action === 'ACTIVATE' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3, gap: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {t('changelog.detailDialog.previousActiveVersion')}
                                </Typography>
                                <Typography variant="h4" fontWeight={700}>
                                    {selectedEntry?.oldValue || '-'}
                                </Typography>
                            </Box>
                            <Typography variant="h4" color="text.secondary" sx={{ mx: 2 }}>→</Typography>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {t('changelog.detailDialog.newActiveVersion')}
                                </Typography>
                                <Typography variant="h4" fontWeight={700} color="accent.main">
                                    {selectedEntry?.newValue || '-'}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', height: '600px' }}>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ pb: 1, color: 'text.secondary' }}>
                                    {t('changelog.columns.oldValue')}
                                </Typography>
                                <Box sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    bgcolor: 'grey.50',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    lineHeight: '20px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: 'text.primary'
                                 }}>
                                    {selectedEntry?.oldValue || '-'}
                                </Box>
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ pb: 1, color: 'text.secondary' }}>
                                    {t('changelog.columns.newValue')}
                                </Typography>
                                <Box sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    bgcolor: 'grey.50',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    lineHeight: '20px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: 'text.primary'
                                }}>
                                    {selectedEntry?.newValue || '-'}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TabChangelog;
